import React, { useContext, useEffect, useState } from "react"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import SavedRequestFormDescSection from "../../../AdminForm/molecules/SavedRequestFormDescSection"
import ModalWithTwoParts from "./ModalWithTwoParts"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import {
	TFormModalMode,
	TOnClick,
} from "components/ProjectSpecificComponents/Interfaces/GlobalInterface"
import { IComment } from "../../atoms/CommentsScrollView/Interfaces"
import CommentsSection from "../CommentsSection"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { ISavedRequestFormField } from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import {
	IToolComment,
	IPOST_Requests_Comment,
	IToolAttachmentMetadata,
} from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import { observer } from "mobx-react"
import { stripHtml } from "string-strip-html"
import LoaderWithText from "components/ProjectNonSpecificComponents/Elements/Loader/LoaderWithText"
import concatWithSpaces from "components/ProjectNonSpecificComponents/Functions/concatWithSpaces"
import SepLine from "../../atoms/SepLine"
import AddedAttachmentsRepr from "../AddedAttachmentsRepr"
import { UserContext } from "components/ProjectSpecificComponents/Contexts/UserContext"
import { lostOrFinishedUserSession } from "components/ProjectSpecificComponents/API/Functions/lostOrFinishedUserSession"

interface Props {
	mode: TFormModalMode
	fields?: ISavedRequestFormField[]
	/**
	 * Tool item's `projectId`.
	 * Required in order to send attachments.
	 */
	projectId?: string
	/**
	 * Tool item's `itemId`.
	 * Required in order to send comments and attachments.
	 */
	itemId?: string
	/**
	 * Action when clicked out of modal or on close button
	 */
	onClose?: TOnClick<HTMLElement | HTMLButtonElement>
	fadeBackground?: boolean
}

const SavedRequestFormModal = observer(
	({ mode, fields, projectId, itemId, onClose = () => {}, fadeBackground }: Props) => {
		const store = useStore()
		const { isMobile, isSmallTablet } = useScreenType()
		const { setUser } = useContext(UserContext)
		const isOrientationVertical = isMobile || isSmallTablet

		const [addedFiles, setAddedFiles] = useState<File[]>([])
		const [commentsData, setCommentsData] = useState<IComment[]>([])
		const [addedAttachmentsData, setAddedAttachmentsData] = useState<IToolAttachmentMetadata[]>()

		const [isReqDataLoading, setReqDataLoading] = useState<boolean>(false)

		useEffect(() => {
			// Clean up state
			setAddedFiles([])
			return () => {
				setAddedFiles([])
				setCommentsData([])
				setAddedAttachmentsData(undefined)
			}
		}, [itemId])

		useEffect(() => {
			if (!itemId) return

			const fetchRequestData = async () => {
				setReqDataLoading(true)
				await Promise.all([fetchAndSetComments(), fetchAndSetAttachments()])
				setReqDataLoading(false)
			}
			fetchRequestData()
		}, [itemId])

		const fetchAndSetComments = async () => {
			const { ok, data, statusCode } = await AppRequests("Requests").get<IToolComment[]>("comments", {
				requestedItemId: itemId,
			})

			if (ok && data) {
				// Transform data
				let commentsData: IComment[] = []
				for (let { author, createdDate, content } of data) {
					commentsData.push({
						author: author.name ?? "",
						text: stripHtml(content ?? "").result,
						timestamp: createdDate,
					})
				}
				setCommentsData(commentsData)
			} else if (statusCode == 401) {
				setUser(undefined)
				lostOrFinishedUserSession(store)
			} else {
				store.setInfoModalConfig({
					titleKey: "infoModal.failed.toFulfillRequest",
					descriptionKey: "infoModal.failed.toRetrieveComments",
					type: "warning",
				})
			}
		}

		const fetchAndSetAttachments = async () => {
			const { ok, data, statusCode } = await AppRequests("Requests").get<IToolAttachmentMetadata[]>(
				"attachments",
				{
					requestedItemId: itemId,
				}
			)

			if (ok && data) {
				setAddedAttachmentsData(data)
			} else if (statusCode == 401) {
				setUser(undefined)
				lostOrFinishedUserSession(store)
			} else {
				store.setInfoModalConfig({
					titleKey: "infoModal.failed.toFulfillRequest",
					descriptionKey: "infoModal.failed.toRetrieveAttachments",
					type: "warning",
				})
			}
		}
		const checkItemIdAndProjectId = () => {
			if (!projectId || !itemId) {
				store.setInfoModalConfig({
					titleKey: "infoModal.failed.toFulfillRequest",
					descriptionKey: "infoModal.failed.toSendMessage",
					type: "warning",
				})
			}
		}
		const sendAttachment = async () => {
			checkItemIdAndProjectId()
			if (!projectId || !itemId) return
			store.setLoaderOverlayOpen(true)

			for (const file of addedFiles) {
				const formData = new FormData()
				formData.append("ProjectId", projectId)
				formData.append("ItemId", itemId)
				formData.append("Name", file.name)
				formData.append("Content", file)
				await AppRequests("Requests").post<FormData>("attachment", formData)
			}
			store.setLoaderOverlayOpen(false)

			setAddedFiles([])
			fetchAndSetAttachments()
		}
		const sendComment = async (comment: string) => {
			checkItemIdAndProjectId()
			if (!itemId) return

			store.setLoaderOverlayOpen(true)

			await AppRequests("Requests").post<IPOST_Requests_Comment>("comment", {
				itemId,
				content: comment,
			})

			store.setLoaderOverlayOpen(false)

			fetchAndSetComments()
		}

		return (
			<ModalWithTwoParts onClose={onClose}>
				{isReqDataLoading ? (
					<HyperDiv
						centered
						direction="column"
						style={{
							width: 1200,
							maxWidth: "90vw",
						}}
					>
						<Whitespace x="2" />
						<LoaderWithText />
						<Whitespace x="2" />
					</HyperDiv>
				) : (
					<HyperDiv
						id="modal-with-header"
						direction={isOrientationVertical ? "column" : "row"}
						style={{
							width: 1200,
							maxWidth: "90vw",
						}}
					>
						<HyperDiv
							flex={2}
							direction="column"
							style={{
								backgroundColor: "var(--light-light-gray)",
							}}
						>
							<HyperDiv
								direction="column"
								style={{
									overflowY: "scroll",
									maxHeight: isOrientationVertical ? "100%" : "90vh",
								}}
							>
								<SavedRequestFormDescSection
									config={fields}
									addedAttachmentsData={addedAttachmentsData}
								/>
							</HyperDiv>
						</HyperDiv>

						<HyperDiv flex={3} direction="column" className="overflow-hidden">
							<CommentsSection
								mode={mode}
								data={commentsData}
								files={addedFiles}
								onClickCommentSend={sendComment}
								onClickAttachmentSend={sendAttachment}
								onUpdateAddedFiles={setAddedFiles}
							/>
							<Whitespace x="2" />
						</HyperDiv>
					</HyperDiv>
				)}
			</ModalWithTwoParts>
		)
	}
)

export default SavedRequestFormModal
